import { faClockRotateLeft, faFileImport } from "@fortawesome/pro-solid-svg-icons";
import { i18n } from "../i18n";
import { useServiceStore } from "../store/service";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type FieldWarning, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

const { t } = i18n.global;

// Shown in place of the Mod-Choice help text while the mod loader is selected: 1.0 breaks mods made for
// earlier versions (Keen asked us to warn about it).
const getModLoaderWarning = (value: unknown): FieldWarning | null =>
  value === "enshroudedEML" ? { key: "ngp/enshrouded/mods/loader_warning" } : null;

const configSections = (isPs5: boolean): ConfigSection[] => {
  const sections: ConfigSection[] = [
    // The server name warning is about Steam's 63-byte name limit, so PS5 gets the plain section.
    { component: isPs5 ? "ConfSection" : "EnshroudedBasicSection", props: { name: "basic" } },
    {
      component: "ConfSection",
      props: { name: "enshroudedMods", fieldWarnings: { enshroudedMods: getModLoaderWarning } },
    },
    { component: "EnshroudedCustomSettings", props: { name: "custom" } },
    { component: "UserGroups" },
    { component: "TableSection", props: { name: "banList", showForm: false } },
  ];
  // No mod loader on PS5: the Mods section stays off even if a form config synced from PC brings it back.
  return isPs5 ? sections.filter((section) => section.props?.name !== "enshroudedMods") : sections;
};

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  // Save Backups (TR-5483) on PC and PS5 alike: both run the same daemon service and both layouts
  // (pre-1.0 slots, 1.0 world0.ksd) are handled daemon-side.
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/enshrouded/SaveBackups.vue")),
    ),
  );
  // Save Import (TR-5484), also on PC and PS5 (Thomas, 2026-10-08): a PS5 server runs the 1.0 layout, which
  // takes a 1.0 world directly and converts a pre-1.0 one at the next start.
  menu[0].items.push(
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/enshrouded/SaveImport.vue")),
    ),
  );
  menu[0].items.push(commonMenuItems.logs);
  return menu;
};

const join = () => {
  window.open(t("ngp/settings/section/enshrouded/link"), "_blank", "noopener,noreferrer");
};

// IP:queryPort is how players reach the server on both platforms: PC through the Steam server list or
// favourites, PS5 through direct IP connect, which also uses the query port.
const queryPortAddress = () => {
  const service = useServiceStore();
  const gameServer = service.ctx.sys?.gameServer;
  const queryPort = service.ctx.service?.config?.queryPort;
  if (!gameServer || !queryPort) {
    return undefined;
  }
  return `${gameServer.serverIp}:${queryPort}`;
};

// The how-to behind the join button walks through the Steam server list, so it is PC-only.
const steamJoin: Partial<GameProfile> = {
  join: {
    buttonTextKey: "ngp/settings/section/enshrouded",
    joinFn: join,
  },
};

// Shared by Enshrouded and EnshroudedPS5 (TR-5475), split on the backend profile like the backend itself.
// Both show IP:queryPort in the header. PS5 uses Keen's own backend instead of Steam, so it gets no Steam
// how-to, and it has no mod loader: the Mods section stays off even if a form config synced from PC brings
// it back.
export default (ctx: ProfileCtx): Partial<GameProfile> => {
  const isPs5 = ctx.profile?.name === "EnshroudedPS5";

  return {
    configSections: configSections(isPs5),
    menu: createMenu(),
    accessDataFn: queryPortAddress,
    statusPageConfig: {
      ...defaultProfile(ctx).statusPageConfig,
      // Enshrouded has no RCON on any platform; customers tried the advertised port and opened tickets.
      showRconPort: false,
      // Players join with a user group password; without groups the game sets a random one (both platforms).
      showJoinPassword: true,
    },
    ...(isPs5 ? {} : steamJoin),
  };
};
